import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Monitor, AlertTriangle, Lightbulb, Film, Zap, Target, Lock } from "lucide-react";
import { isPSSOutdated } from "../../components/common/PSSUpdateBanner";
import { getLatestPSSVersion } from "../../services/configService";

const DOWNLOAD_WINDOWS = "/downloads/Party%20Sharing%20Studio%20Setup%201.1.0.rar?v=20260511-1";
const DOWNLOAD_MAC     = "/downloads/Party%20Sharing%20Studio-1.1.0.dmg?v=20260511-1";

function detectOS() {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (ua.includes("mac")) return "mac";
  return "windows";
}

const CompanionInstall = () => {
  const { t } = useTranslation(["companion", "common"]);
  const [status, setStatus] = useState("checking");
  const [installedVersion, setInstalledVersion] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  const [latestDownloads, setLatestDownloads] = useState({ windows: DOWNLOAD_WINDOWS, mac: DOWNLOAD_MAC });
  const os = detectOS();

  const fetchLatestVersion = () => {
    getLatestPSSVersion()
      .then((res) => {
        const data = res.data;
        if (data?.version) setLatestVersion(data.version);
        if (data?.windows || data?.mac) {
          setLatestDownloads({
            windows: data.windows || DOWNLOAD_WINDOWS,
            mac: data.mac || DOWNLOAD_MAC,
          });
        }
      })
      .catch(() => { /* silently ignore — we still show current downloads */ });
  };

  useEffect(() => {
    fetchLatestVersion();
    import("../../services/videoCompressionService").then((mod) => {
      mod._resetCompanionStatus?.();
      mod.checkCompanion().then((ok) => {
        setStatus(ok ? "running" : "not-running");
        setInstalledVersion(mod.getCompanionVersion?.() ?? null);
      });
    });
  }, []);

  const recheckStatus = () => {
    setStatus("checking");
    setInstalledVersion(null);
    import("../../services/videoCompressionService").then((mod) => {
      mod._resetCompanionStatus?.();
      mod.checkCompanion().then((ok) => {
        setStatus(ok ? "running" : "not-running");
        setInstalledVersion(mod.getCompanionVersion?.() ?? null);
      });
    });
  };

  const isOutdated = status === "running" && isPSSOutdated(installedVersion, latestVersion);

  const statusConfig = {
    checking:      { dot: "⏳", label: t("status.checkingLabel"),         sub: t("status.checkingSub"),                                                                      bg: "rgba(255,255,255,0.9)", border: "rgba(0,0,0,0.15)",  labelColor: "#000",     dotBg: "#9ca3af" },
    running:       { dot: "●",  label: isOutdated ? t("status.runningLabelOutdated") : t("status.runningLabel"),        sub: isOutdated ? t("status.runningSubOutdated", { installed: installedVersion, latest: latestVersion }) : t("status.runningSub", { installed: installedVersion || t("status.unknownVersion") }),  bg: isOutdated ? "rgba(254,243,199,0.95)" : "rgba(220,252,231,0.95)",  border: isOutdated ? "rgba(251,191,36,0.5)" : "rgba(34,197,94,0.35)",   labelColor: "#000",  dotBg: isOutdated ? "#b45309" : "#15803d" },
    "not-running": { dot: "●",  label: t("status.notRunningLabel"),    sub: t("status.notRunningSub"),    bg: "rgba(254,242,242,0.95)",  border: "rgba(239,68,68,0.35)",   labelColor: "#000",  dotBg: "#b91c1c" },
  };
  const sc = statusConfig[status];

  const macSteps = [
    { n: 1, label: t("installSteps.downloadLabel"),           action: <a href={latestDownloads.mac} download style={styles.downloadBtn}>⬇&nbsp; {t("installSteps.mac.downloadAction")}</a> },
    { n: 2, label: t("installSteps.mac.step2Label"), action: <span style={styles.stepNote}>{t("installSteps.mac.step2Note")}</span> },
    { n: 3, label: t("installSteps.mac.step3Label"),         action: <span style={styles.stepNote}>{t("installSteps.mac.step3Note")}</span> },
  ];
  const winSteps = [
    { n: 1, label: t("installSteps.downloadLabel"),           action: <a href={latestDownloads.windows} download style={styles.downloadBtn}>⬇&nbsp; {t("installSteps.windows.downloadAction")}</a> },
    { n: 2, label: t("installSteps.windows.step2Label"),        action: <span style={styles.stepNote}>{t("installSteps.windows.step2Note")}</span> },
    { n: 3, label: t("installSteps.windows.step3Label"),          action: <span style={styles.stepNote}>{t("installSteps.windows.step3Note")}</span> },
  ];
  const steps = os === "mac" ? macSteps : winSteps;

  return (
    <div style={styles.page}>

      {/* ── Hero ── */}
      <div style={styles.hero}>
        <div style={styles.heroLeft}>
          <div style={styles.heroIconWrap}><Monitor size={26} strokeWidth={1.75} /></div>
          <div>
            <h2 style={styles.heroTitle}>{t("hero.title")}</h2>
            <p style={styles.heroSub}>
              {t("hero.description")}
            </p>
            <div style={styles.bannerCtaWrap}>
              <a href={os === "mac" ? latestDownloads.mac : latestDownloads.windows} download style={styles.primaryDownloadBtn}>
                {t("hero.downloadCta")}
              </a>
              <span style={styles.downloadNote}>
                {t("hero.termsNote")}
              </span>
              <span style={styles.ffmpegNote}>
                {t("hero.ffmpegNote")}
              </span>
            </div>
            {/* Status pill inside hero */}
            <div style={{ ...styles.statusPill, background: sc.bg, borderColor: sc.border }}>
              <span style={{ ...styles.statusDotCircle, background: sc.dotBg, color: sc.labelColor }}>{sc.dot}</span>
              <span style={{ ...styles.statusLabel, color: sc.labelColor }}>{sc.label}</span>
              <span style={styles.statusSub}>{sc.sub}</span>
              <button style={styles.recheckBtn} onClick={recheckStatus}>↺ {t("recheck")}</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Version update banner ── */}
      {isOutdated && (
        <div style={styles.updateBanner}>
          <span style={{ flexShrink: 0, display: "flex" }}><AlertTriangle size={22} strokeWidth={2} /></span>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
            <strong style={{ fontSize: 13.5, fontWeight: 700, color: "#92400e" }}>
              {t("updateBanner.title")}
            </strong>
            <span style={{ fontSize: 13, color: "#78350f", lineHeight: 1.55 }}>
              {t("updateBanner.youHave")} <strong>v{installedVersion}</strong> {t("updateBanner.butLatestIs")}{" "}
              <strong>v{latestVersion}</strong>. {t("updateBanner.downloadInstruction")}{" "}
              <strong>↺ {t("recheck")}</strong> {t("updateBanner.confirmSuffix")}
            </span>
          </div>
          <a
            href={os === "mac" ? latestDownloads.mac : latestDownloads.windows}
            download
            style={styles.updateBtn}
          >
            ⬇&nbsp; {t("updateBanner.downloadVersion", { version: latestVersion })}
          </a>
        </div>
      )}

      {/* ── Install Steps (TOP priority) ── */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionBadge}>{t("installSteps.badge")}</span>
          <h3 style={styles.sectionTitle}>{t("installSteps.title")}</h3>
        </div>
        <div style={styles.steps}>
          {steps.map((s) => (
            <div key={s.n} style={styles.step}>
              <div style={styles.stepNum}>{s.n}</div>
              <div style={styles.stepContent}>
                <div style={styles.stepLabel}>{s.label}</div>
                <div style={{ marginTop: 10 }}>{s.action}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Already installed hint ── */}
      {status === "not-running" && (
        <div style={styles.hintBox}>
          <span style={{ display: "flex" }}><Lightbulb size={17} strokeWidth={2} /></span>
          <span>
            <strong style={{ color: "#fcd34d" }}>{t("alreadyInstalled.title")}</strong>{" "}
            {os === "mac"
              ? <>{t("alreadyInstalled.mac.prefix")} <strong>{t("alreadyInstalled.mac.menuBar")}</strong> {t("alreadyInstalled.mac.suffix")} <strong>↺ {t("recheck")}</strong> {t("alreadyInstalled.above")}</>
              : <>{t("alreadyInstalled.windows.prefix")} <strong>{t("alreadyInstalled.windows.taskbar")}</strong>. {t("alreadyInstalled.windows.suffix")} <strong>↺ {t("recheck")}</strong> {t("alreadyInstalled.above")}</>
            }
          </span>
        </div>
      )}

      {/* ── Divider ── */}
      <div style={styles.divider} />

      {/* ── Benefits ── */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionBadge}>{t("benefits.badge")}</span>
          <h3 style={styles.sectionTitle}>{t("benefits.title")}</h3>
        </div>
        <div style={styles.benefitsGrid}>
          {[
            { icon: Film, title: t("benefits.fileSize.title"),   desc: t("benefits.fileSize.desc") },
            { icon: Zap, title: t("benefits.faster.title"),     desc: t("benefits.faster.desc") },
            { icon: Target, title: t("benefits.quality.title"),  desc: t("benefits.quality.desc") },
            { icon: Lock, title: t("benefits.local.title"),     desc: t("benefits.local.desc") },
          ].map((b) => (
            <div key={b.title} style={styles.benefitCard}>
              <div style={styles.benefitIcon}><b.icon size={22} strokeWidth={1.75} /></div>
              <strong style={styles.benefitTitle}>{b.title}</strong>
              <p style={styles.benefitDesc}>{b.desc}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

const styles = {
  page: { maxWidth: 800, margin: "0 auto", padding: "32px 8px 56px", color: "#000" },

  /* Hero */
  hero: {
    background: "linear-gradient(135deg, rgba(124,58,237,0.22) 0%, rgba(124,58,237,0.06) 100%)",
    border: "1px solid rgba(124,58,237,0.28)",
    borderRadius: 16,
    padding: "28px 32px",
    marginBottom: 32,
  },
  heroLeft:    { display: "flex", alignItems: "flex-start", gap: 20 },
  heroIconWrap:{
    width: 56, height: 56, borderRadius: 14, background: "rgba(124,58,237,0.1)", color: "#7C3AED",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  heroTitle:   { fontSize: 24, fontWeight: 800, color: "#000", margin: "0 0 8px", letterSpacing: "-0.3px" },
  heroSub:     { fontSize: 14, color: "#000", margin: "0 0 16px", lineHeight: 1.7, maxWidth: 620 },
  bannerCtaWrap: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, marginBottom: 18 },
  primaryDownloadBtn: { display: "inline-flex", alignItems: "center", gap: 8, background: "linear-gradient(135deg,#7C3AED,#9333ea)", color: "#fff", padding: "11px 24px", borderRadius: 9, fontSize: 13.5, fontWeight: 700, textDecoration: "none", boxShadow: "0 3px 14px rgba(124,58,237,0.4)", letterSpacing: "0.01em" },
  downloadNote: { fontSize: 12.5, color: "#000", lineHeight: 1.6, maxWidth: 640 },
  ffmpegNote: { fontSize: 12.5, color: "#000", lineHeight: 1.6, maxWidth: 640 },

  /* Status pill */
  statusPill:  { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "10px 16px", borderRadius: 10, border: "1px solid" },
  statusDotCircle: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  statusLabel: { fontWeight: 700, fontSize: 13.5 },
  statusSub:   { fontSize: 12.5, color: "#000", flex: 1 },
  recheckBtn:  { flexShrink: 0, background: "rgba(255,255,255,0.8)", border: "1px solid rgba(0,0,0,0.2)", color: "#000", borderRadius: 7, padding: "6px 14px", cursor: "pointer", fontSize: 12.5, whiteSpace: "nowrap" },

  /* Sections */
  section:       { marginBottom: 36 },
  sectionHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 },
  sectionBadge:  { fontSize: 10, fontWeight: 700, color: "#000", background: "rgba(255,255,255,0.7)", padding: "3px 8px", borderRadius: 4, letterSpacing: "0.08em", border: "1px solid rgba(0,0,0,0.15)" },
  sectionTitle:  { fontSize: 16, fontWeight: 700, color: "#000", margin: 0 },

  /* Steps */
  steps:       { display: "flex", flexDirection: "column", gap: 10 },
  step:        { display: "flex", alignItems: "flex-start", gap: 18, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "20px 24px", transition: "border-color 0.2s" },
  stepNum:     { width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#7C3AED,#9333ea)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, flexShrink: 0, boxShadow: "0 2px 10px rgba(124,58,237,0.45)", marginTop: 2 },
  stepContent: { flex: 1 },
  stepLabel:   { fontSize: 15, fontWeight: 600, color: "#000" },
  stepNote:    { fontSize: 13, color: "#000", lineHeight: 1.6 },
  downloadBtn: { display: "inline-flex", alignItems: "center", gap: 8, background: "linear-gradient(135deg,#7C3AED,#9333ea)", color: "#fff", padding: "11px 24px", borderRadius: 9, fontSize: 13.5, fontWeight: 700, textDecoration: "none", boxShadow: "0 3px 14px rgba(124,58,237,0.4)", letterSpacing: "0.01em" },

  /* Hint */
  hintBox: { display: "flex", alignItems: "flex-start", gap: 14, background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 12, padding: "16px 20px", fontSize: 13.5, color: "#000", lineHeight: 1.65, marginBottom: 32 },

  /* Update banner */
  updateBanner: { display: "flex", alignItems: "center", gap: 14, background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.5)", borderRadius: 12, padding: "16px 20px", marginBottom: 28, flexWrap: "wrap" },
  updateBtn:    { flexShrink: 0, background: "linear-gradient(135deg,#d97706,#b45309)", color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" },

  /* Divider */
  divider: { borderTop: "1px solid rgba(255,255,255,0.06)", marginBottom: 36 },

  /* Benefits */
  benefitsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  benefitCard:  { background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "20px 22px" },
  benefitIcon:  { color: "#7C3AED", marginBottom: 12 },
  benefitTitle: { fontSize: 14, color: "#000", display: "block", marginBottom: 7, fontWeight: 700 },
  benefitDesc:  { fontSize: 13, color: "#000", margin: 0, lineHeight: 1.6 },
};

export default CompanionInstall;
